/**
 * County entity index page with state dropdown + search
 *
 * State dropdown filters counties, search box provides text search within results.
 * States are loaded dynamically from GraphQL.
 */

import { canonicalPath } from '@electinfo/common';
import { belowHeaderInsertPoint } from './page-header';

const PAGE_SIZE = 50;

/** Fixed document (persisted-query list, sites#277); limit 50 = page size. */
const COUNTIES_QUERY = `
query Counties($state: String, $search: String, $offset: Int!) {
  counties(
    where: { state: { eq: $state }, name: { contains: $search } }
    sort: [{ name: ASC }]
    limit: 50
    offset: $offset
  ) {
    id
    name
    state
    county_fips
    locatedInStates {
      id
      name
      state
    }
  }
  countiesConnection(where: { state: { eq: $state }, name: { contains: $search } }) {
    totalCount
  }
}
`;

interface GraphQLRequest {
  query: string;
  variables: Record<string, unknown>;
}

interface StateInfo {
  name: string | null;
  state: string;
}

interface CountyResult {
  id: string;
  name: string;
  state?: string;
  county_fips?: string;
  locatedInStates?: Array<{
    id?: string;
    name?: string;
    state?: string;
  }>;
}

interface GraphQLResponse {
  data?: {
    counties: CountyResult[];
    countiesConnection?: {
      totalCount: number;
    };
  };
  errors?: Array<{ message: string }>;
}

class CountyIndex {
  private currentState = '';
  private currentQuery = '';
  private currentPage = 1;
  private totalResults = 0;
  private states: StateInfo[] = [];

  constructor() {
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  private async setup(): Promise<void> {
    this.injectStyles();
    await this.loadStates();
    this.insertUI();
    this.loadFromURL();
  }

  private async loadStates(): Promise<void> {
    try {
      const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: '{ states(sort: [{ name: ASC }]) { name state } }'
        })
      });

      if (res.ok) {
        const data = await res.json();
        this.states = (data.data?.states || []).filter((s: StateInfo) => s.name);
      }
    } catch (err) {
      console.error('Failed to load states:', err);
    }
  }

  private getStateName(code: string): string {
    const state = this.states.find(s => s.state === code);
    return state?.name || code;
  }

  private injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .county-container {
        background: white;
        padding: 20px;
        border-radius: 8px;
        margin-bottom: 20px;
        border: 1px solid var(--border, #e2e8f0);
      }
      .county-filters {
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
        margin-bottom: 15px;
      }
      .county-filters label {
        font-weight: 600;
        color: var(--primary, #1a365d);
      }
      .county-filters select {
        padding: 12px 16px;
        font-size: 1rem;
        border: 2px solid var(--border, #e2e8f0);
        border-radius: 6px;
        font-family: inherit;
        min-width: 200px;
        cursor: pointer;
      }
      .county-filters select:focus {
        outline: none;
        border-color: var(--secondary, #2c5282);
      }
      .county-search-form {
        display: flex;
        gap: 10px;
        flex: 1;
        min-width: 250px;
      }
      .county-search-form input {
        flex: 1;
        padding: 12px 16px;
        font-size: 1rem;
        border: 2px solid var(--border, #e2e8f0);
        border-radius: 6px;
        font-family: inherit;
      }
      .county-search-form input:focus {
        outline: none;
        border-color: var(--secondary, #2c5282);
      }
      .county-search-form button {
        padding: 12px 24px;
        font-size: 1rem;
        background: var(--secondary, #2c5282);
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-family: inherit;
      }
      .county-search-form button:hover {
        background: var(--primary, #1a365d);
      }
      .county-results {
        margin-top: 15px;
      }
      .county-results-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--border, #e2e8f0);
      }
      .county-results-header h3 {
        margin: 0;
        color: var(--primary, #1a365d);
      }
      .county-clear {
        background: none;
        border: 1px solid var(--border, #e2e8f0);
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9em;
      }
      .county-clear:hover {
        background: var(--bg, #f7fafc);
      }
      .county-result {
        display: block;
        padding: 12px 16px;
        border: 1px solid var(--border, #e2e8f0);
        border-radius: 6px;
        margin-bottom: 8px;
        text-decoration: none;
        color: inherit;
      }
      .county-result:hover {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .county-result-name {
        font-weight: 600;
        color: var(--primary, #1a365d);
      }
      .county-result-meta {
        font-size: 0.9em;
        color: #718096;
        margin-top: 2px;
      }
      .county-message {
        padding: 20px;
        text-align: center;
        color: #718096;
      }
      .county-error {
        color: #c53030;
        background: #fff5f5;
        border-radius: 6px;
      }
      .county-loading {
        text-align: center;
        padding: 40px;
        color: #718096;
      }
      .county-loading::after {
        content: '';
        display: inline-block;
        width: 20px;
        height: 20px;
        border: 2px solid #e2e8f0;
        border-top-color: var(--secondary, #2c5282);
        border-radius: 50%;
        animation: county-spin 0.8s linear infinite;
        margin-left: 10px;
        vertical-align: middle;
      }
      @keyframes county-spin {
        to { transform: rotate(360deg); }
      }
      .county-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid var(--border, #e2e8f0);
        flex-wrap: wrap;
      }
      .county-pagination-info {
        width: 100%;
        text-align: center;
        color: #718096;
        font-size: 0.9em;
        margin-bottom: 10px;
      }
      .county-pagination button {
        padding: 8px 12px;
        border: 1px solid var(--border, #e2e8f0);
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9em;
        min-width: 40px;
      }
      .county-pagination button:hover:not(:disabled) {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .county-pagination button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .county-pagination button.active {
        background: var(--secondary, #2c5282);
        color: white;
        border-color: var(--secondary, #2c5282);
      }
      .county-index-hidden {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  private insertUI(): void {
    const container = document.createElement('div');
    container.className = 'county-container';

    const stateOptions = this.states
      .map(s => `<option value="${s.state}">${s.name}</option>`)
      .join('');

    container.innerHTML = `
      <div class="county-filters">
        <label for="county-state-select">State:</label>
        <select id="county-state-select">
          <option value="">All States</option>
          ${stateOptions}
        </select>
        <form class="county-search-form" id="county-search-form">
          <input type="text" id="county-search-input" placeholder="Search counties..." autocomplete="off">
          <button type="submit">Search</button>
        </form>
      </div>
      <div class="county-results" id="county-results">
        <div class="county-results-header">
          <h3 id="county-results-title">All Counties</h3>
          <button type="button" class="county-clear" id="county-clear" style="display: none;">Clear Filters</button>
        </div>
        <div id="county-results-list"></div>
        <div id="county-pagination"></div>
      </div>
    `;

    const listView = document.getElementById('list-view');
    if (listView) {
      listView.appendChild(container);
    } else {
      const insertPoint = belowHeaderInsertPoint();
      if (insertPoint?.parentNode) {
        insertPoint.parentNode.insertBefore(container, insertPoint);
      }
    }

    // Hide any existing static content
    document.querySelectorAll('.alphabetical-index, section > ul, main > section').forEach(el => {
      el.classList.add('county-index-hidden');
    });

    // Event listeners
    document.getElementById('county-state-select')?.addEventListener('change', (e) => {
      const select = e.target as HTMLSelectElement;
      this.loadCounties({ state: select.value, query: this.currentQuery, page: 1 });
    });

    document.getElementById('county-search-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = document.getElementById('county-search-input') as HTMLInputElement;
      this.loadCounties({ state: this.currentState, query: input?.value.trim() || '', page: 1 });
    });

    document.getElementById('county-clear')?.addEventListener('click', () => {
      const stateSelect = document.getElementById('county-state-select') as HTMLSelectElement;
      const searchInput = document.getElementById('county-search-input') as HTMLInputElement;
      if (stateSelect) stateSelect.value = '';
      if (searchInput) searchInput.value = '';
      this.loadCounties({ state: '', query: '', page: 1 });
    });

    window.addEventListener('popstate', () => this.loadFromURL());
  }

  private loadFromURL(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state') || '';
    const query = urlParams.get('q') || '';
    const page = parseInt(urlParams.get('page') || '1', 10) || 1;

    const stateSelect = document.getElementById('county-state-select') as HTMLSelectElement;
    const searchInput = document.getElementById('county-search-input') as HTMLInputElement;
    if (stateSelect) stateSelect.value = state;
    if (searchInput) searchInput.value = query;

    this.loadCounties({ state, query, page, updateHistory: false });
  }

  private async loadCounties(options: { state?: string; query?: string; page?: number; updateHistory?: boolean } = {}): Promise<void> {
    const state = options.state ?? this.currentState;
    const query = options.query ?? this.currentQuery;
    const page = options.page || 1;
    const updateHistory = options.updateHistory !== false;

    const resultsList = document.getElementById('county-results-list');
    const paginationContainer = document.getElementById('county-pagination');
    const resultsTitle = document.getElementById('county-results-title');
    const clearButton = document.getElementById('county-clear');

    this.currentState = state;
    this.currentQuery = query;
    this.currentPage = page;

    if (resultsList) resultsList.innerHTML = '<div class="county-loading">Loading</div>';
    if (paginationContainer) paginationContainer.innerHTML = '';

    // Update title
    let title = 'All Counties';
    if (state && query) {
      title = `"${query}" in ${this.getStateName(state)}`;
    } else if (state) {
      title = `${this.getStateName(state)} Counties`;
    } else if (query) {
      title = `Search: "${query}"`;
    }
    if (resultsTitle) resultsTitle.textContent = title;
    if (clearButton) clearButton.style.display = (state || query) ? 'block' : 'none';

    if (updateHistory) {
      this.updateURL(state, query, page);
    }

    const graphqlQuery = this.buildGraphQLQuery(state, query, page);

    try {
      const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(graphqlQuery)
      });

      if (!res.ok) {
        if (resultsList) resultsList.innerHTML = `<p class="county-message county-error">Failed to load: ${res.status}</p>`;
        return;
      }

      const data: GraphQLResponse = await res.json();

      if (data.errors) {
        if (resultsList) resultsList.innerHTML = `<p class="county-message county-error">Error: ${data.errors[0]?.message || 'GraphQL error'}</p>`;
        return;
      }

      const counties = data.data?.counties || [];
      this.totalResults = data.data?.countiesConnection?.totalCount || counties.length;

      this.displayResults(counties);
      this.displayPagination(page, this.totalResults);

    } catch (err) {
      if (resultsList) resultsList.innerHTML = `<p class="county-message county-error">Error: ${(err as Error).message}</p>`;
    }
  }

  private buildGraphQLQuery(state: string, searchQuery: string, page: number): GraphQLRequest {
    return {
      query: COUNTIES_QUERY,
      variables: {
        offset: (page - 1) * PAGE_SIZE,
        ...(state && { state }),
        ...(searchQuery && { search: searchQuery })
      }
    };
  }

  private displayResults(counties: CountyResult[]): void {
    const resultsList = document.getElementById('county-results-list');
    if (!resultsList) return;

    if (!counties.length) {
      resultsList.innerHTML = '<p class="county-message">No counties found.</p>';
      return;
    }

    const html = counties.map(county => {
      const name = county.name || '';
      const stateCode = county.state || '';
      const stateName = county.locatedInStates?.[0]?.name || this.getStateName(stateCode);
      const url = `/${canonicalPath('County', county.id)}/`;

      return `
        <a href="${url}" class="county-result">
          <div class="county-result-name">${this.escapeHtml(name)}</div>
          <div class="county-result-meta">${this.escapeHtml(stateName)}</div>
        </a>
      `;
    }).join('');

    resultsList.innerHTML = html;
  }

  private displayPagination(page: number, total: number): void {
    const paginationContainer = document.getElementById('county-pagination');
    if (!paginationContainer) return;

    const totalPages = Math.ceil(total / PAGE_SIZE);

    if (totalPages <= 1) {
      paginationContainer.innerHTML = total > 0
        ? `<div class="county-pagination-info">Showing all ${total.toLocaleString()} counties</div>`
        : '';
      return;
    }

    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, total);

    let html = `<div class="county-pagination-info">Showing ${start.toLocaleString()}-${end.toLocaleString()} of ${total.toLocaleString()} counties</div>`;
    html += '<div class="county-pagination">';
    html += `<button type="button" data-page="1" ${page === 1 ? 'disabled' : ''}>First</button>`;
    html += `<button type="button" data-page="${page - 1}" ${page === 1 ? 'disabled' : ''}>Prev</button>`;

    const pageNumbers = this.getPageNumbers(page, totalPages);
    let lastNum = 0;

    for (const num of pageNumbers) {
      if (num - lastNum > 1) {
        html += '<span style="padding: 8px 4px; color: #718096;">...</span>';
      }
      html += `<button type="button" data-page="${num}" class="${num === page ? 'active' : ''}">${num}</button>`;
      lastNum = num;
    }

    html += `<button type="button" data-page="${page + 1}" ${page === totalPages ? 'disabled' : ''}>Next</button>`;
    html += `<button type="button" data-page="${totalPages}" ${page === totalPages ? 'disabled' : ''}>Last</button>`;
    html += '</div>';

    paginationContainer.innerHTML = html;

    paginationContainer.querySelectorAll('button[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetPage = parseInt((btn as HTMLButtonElement).dataset.page || '1', 10);
        if (targetPage >= 1 && targetPage <= totalPages && targetPage !== page) {
          this.loadCounties({ page: targetPage });
          document.getElementById('county-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  private getPageNumbers(current: number, total: number): number[] {
    const pages: number[] = [];
    const delta = 2;

    pages.push(1);
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      pages.push(i);
    }
    if (total > 1) pages.push(total);

    return [...new Set(pages)].sort((a, b) => a - b);
  }

  private updateURL(state: string, query: string, page: number): void {
    const url = new URL(window.location.href);

    if (state) {
      url.searchParams.set('state', state);
    } else {
      url.searchParams.delete('state');
    }

    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    window.history.pushState({ state, query, page }, '', url.toString());
  }

  private escapeHtml(str: string | undefined): string {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

// Auto-initialize
new CountyIndex();
